interface TechnicalDetailsProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TechnicalDetails({ isOpen, onClose }: TechnicalDetailsProps) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          &times;
        </button>

        <h1>Technical Documentation</h1>
        <p className="subtitle">Hydro-NM: New Mexico Hydrology &amp; Drainage Analysis</p>

        <section>
          <h2>Overview</h2>
          <p>
            Hydro-NM is a web-based hydrology analysis platform for New Mexico, providing
            point-and-click access to hydrological data for civil engineers, highway designers,
            and water resource professionals. Click anywhere on the map to retrieve peak flow
            estimates, precipitation frequency data, and soil characteristics.
          </p>
        </section>

        <section>
          <h2>Data Sources</h2>

          <h3>Peak Flow Estimation</h3>
          <p>
            Peak streamflow estimates use <strong>USGS regional regression equations</strong> developed
            specifically for New Mexico ungauged sites.
          </p>
          <div className="reference-box">
            <strong>Reference:</strong> Bell, M.T., and Tillery, A.C., 2023, Methods for estimating
            peak streamflow at ungaged sites in New Mexico: U.S. Geological Survey Scientific
            Investigations Report 2023-5058.
            <br />
            <a href="https://doi.org/10.3133/sir20235058" target="_blank" rel="noopener noreferrer">
              https://doi.org/10.3133/sir20235058
            </a>
          </div>

          <h3>Precipitation Frequency</h3>
          <p>
            Precipitation frequency estimates are based on <strong>NOAA Atlas 14</strong>, the
            official precipitation frequency atlas for the United States.
          </p>
          <div className="reference-box">
            <strong>Source:</strong> NOAA/NWS Hydrometeorological Design Studies Center
            <br />
            <a href="https://hdsc.nws.noaa.gov/pfds/" target="_blank" rel="noopener noreferrer">
              NOAA Precipitation Frequency Data Server (PFDS)
            </a>
          </div>

          <h3>Soil Data</h3>
          <p>
            Hydrologic soil group, drainage class, and soil texture from the <strong>NRCS SSURGO</strong> database.
          </p>
          <div className="reference-box">
            <strong>Source:</strong> USDA Natural Resources Conservation Service
            <br />
            <a href="https://websoilsurvey.nrcs.usda.gov/" target="_blank" rel="noopener noreferrer">
              Web Soil Survey
            </a>
          </div>

          <h3>Elevation Data</h3>
          <p>
            Digital Elevation Models (DEMs) from the <strong>USGS 3D Elevation Program (3DEP)</strong>,
            1/3 arc-second resolution (~10 meter).
          </p>
          <div className="reference-box">
            <strong>Source:</strong> USGS National Map
            <br />
            <a href="https://www.usgs.gov/3d-elevation-program" target="_blank" rel="noopener noreferrer">
              USGS 3DEP
            </a>
          </div>
        </section>

        <section>
          <h2>Calculation Methods</h2>

          <h3>USGS Regional Regression Equations</h3>
          <p>
            Peak flows for various return periods (2, 5, 10, 25, 50, 100, 200, 500 years) are
            estimated using multiple regression equations of the form:
          </p>
          <div className="equation">
            Q<sub>T</sub> = a &times; DRNAREA<sup>b</sup> &times; ELEV<sup>c</sup> &times; PRECIP<sup>d</sup>
          </div>
          <p>Where:</p>
          <ul>
            <li><strong>Q<sub>T</sub></strong> = Peak flow for return period T (cfs)</li>
            <li><strong>DRNAREA</strong> = Drainage area (square miles)</li>
            <li><strong>ELEV</strong> = Mean watershed elevation (feet / 1000)</li>
            <li><strong>PRECIP</strong> = Mean annual precipitation (inches)</li>
            <li><strong>a, b, c, d</strong> = Regression coefficients by return period</li>
          </ul>
          <p>
            90% confidence intervals are calculated using the standard error of estimate
            in log-transformed space.
          </p>

          <h3>SCS Curve Number Method</h3>
          <p>
            Runoff depth is estimated using the NRCS (formerly SCS) Curve Number method:
          </p>
          <div className="equation">
            Q = (P - I<sub>a</sub>)<sup>2</sup> / (P - I<sub>a</sub> + S)
          </div>
          <p>Where:</p>
          <ul>
            <li><strong>Q</strong> = Runoff depth (inches)</li>
            <li><strong>P</strong> = Precipitation depth (inches)</li>
            <li><strong>S</strong> = Potential maximum retention = (1000/CN) - 10</li>
            <li><strong>I<sub>a</sub></strong> = Initial abstraction = 0.2S</li>
            <li><strong>CN</strong> = Curve number (function of soil group and land cover)</li>
          </ul>

          <h3>Time of Concentration</h3>
          <p>
            The Kirpich formula is used for time of concentration estimation:
          </p>
          <div className="equation">
            T<sub>c</sub> = 0.0078 &times; L<sup>0.77</sup> &times; S<sup>-0.385</sup>
          </div>
          <p>Where:</p>
          <ul>
            <li><strong>T<sub>c</sub></strong> = Time of concentration (minutes)</li>
            <li><strong>L</strong> = Longest flow path (feet)</li>
            <li><strong>S</strong> = Average watershed slope (ft/ft)</li>
          </ul>

          <h3>Rational Method</h3>
          <p>
            For small urban watersheds (&lt;200 acres), the Rational Method applies:
          </p>
          <div className="equation">
            Q = C &times; i &times; A
          </div>
          <p>Where:</p>
          <ul>
            <li><strong>Q</strong> = Peak flow (cfs)</li>
            <li><strong>C</strong> = Runoff coefficient (0-1)</li>
            <li><strong>i</strong> = Rainfall intensity (in/hr) for duration = T<sub>c</sub></li>
            <li><strong>A</strong> = Drainage area (acres)</li>
          </ul>
        </section>

        <section>
          <h2>Technology Stack</h2>

          <h3>Frontend</h3>
          <ul>
            <li><strong>React 18</strong> - UI framework</li>
            <li><strong>TypeScript</strong> - Type-safe JavaScript</li>
            <li><strong>MapLibre GL JS</strong> - Vector map rendering</li>
            <li><strong>Deck.gl</strong> - High-performance data visualization</li>
            <li><strong>Vite</strong> - Build tool and dev server</li>
          </ul>

          <h3>Backend</h3>
          <ul>
            <li><strong>Python 3.11</strong> - Server runtime</li>
            <li><strong>FastAPI</strong> - Async web framework</li>
            <li><strong>PySheds</strong> - Watershed delineation</li>
            <li><strong>GDAL/Rasterio</strong> - Geospatial raster processing</li>
            <li><strong>GeoPandas</strong> - Geospatial vector processing</li>
          </ul>

          <h3>Infrastructure</h3>
          <ul>
            <li><strong>Apache</strong> - Web server with reverse proxy</li>
            <li><strong>systemd</strong> - Service management</li>
            <li><strong>Miniconda</strong> - Python environment management</li>
          </ul>
        </section>

        <section>
          <h2>Current Limitations</h2>
          <ul className="limitations-list">
            <li>
              <strong>Precipitation data:</strong> Currently using approximate values based on
              regional patterns. Full NOAA Atlas 14 API integration pending.
            </li>
            <li>
              <strong>Soil data:</strong> Using simplified lookup based on location. Full SSURGO
              database integration planned.
            </li>
            <li>
              <strong>Watershed delineation:</strong> Temporarily disabled pending DEM preprocessing
              for acceptable response times.
            </li>
            <li>
              <strong>Coverage:</strong> Currently optimized for central New Mexico. Statewide
              coverage requires additional DEM tiles.
            </li>
            <li>
              <strong>Regression coefficients:</strong> Using simplified coefficients. Production
              version will use full USGS regional equations.
            </li>
          </ul>
        </section>

        <section>
          <h2>References</h2>
          <ul className="references-list">
            <li>
              Bell, M.T., and Tillery, A.C., 2023, <em>Methods for estimating peak streamflow
              at ungaged sites in New Mexico</em>: U.S. Geological Survey Scientific Investigations
              Report 2023-5058, 67 p.
            </li>
            <li>
              Bonnin, G.M., et al., 2011, <em>NOAA Atlas 14: Precipitation-Frequency Atlas of
              the United States, Volume 1, Version 5</em>: Semiarid Southwest.
            </li>
            <li>
              USDA-NRCS, 2004, <em>National Engineering Handbook, Part 630: Hydrology</em>,
              Chapter 9: Hydrologic Soil-Cover Complexes.
            </li>
            <li>
              FHWA, 2012, <em>HDS-5: Hydraulic Design of Highway Culverts</em>, Third Edition,
              Publication No. FHWA-HIF-12-026.
            </li>
            <li>
              FHWA, 2009, <em>HEC-22: Urban Drainage Design Manual</em>, Third Edition,
              Publication No. FHWA-NHI-10-009.
            </li>
          </ul>
        </section>

        <footer className="modal-footer">
          <p>
            Hydro-NM is an open-source project for educational and professional use.
            Always verify results with authoritative sources for critical applications.
          </p>
          <p className="version">Version 0.1.0 | December 2024</p>
        </footer>
      </div>
    </div>
  );
}
